import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Button, Input } from '@popety-io/ui'
import { ActionPanel } from './ActionPanel'
import { useActionPanels } from '../../contexts/ActionPanelsContext'
import styled from 'styled-components'

const TagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`

const TagItem = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: ${({ $color }) => $color};
  color: #ffffff;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }
`

const AddTagContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`

const TagManagementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-top: 16px;
`

const TagManagementItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #f8f8f8;
  border-radius: 6px;
  font-size: 13px;
`

const ColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ $color }) => $color};
  border-radius: 50%;
`

interface TagsPanelProps {
  entityId: string
  entityType: 'land' | 'building' | 'group_land'
  tags?: any[]
  loading?: boolean
  addTag?: (tagValue: string, color: string) => Promise<void>
  removeTag?: (tagId: number) => Promise<void>
  refreshTags?: () => Promise<void>
}

const getTagColor = (tag: any) => {
  if (tag.color) return tag.color
  if (tag.tag?.color) return tag.tag.color
  return '#6b6b6b'
}

const getTagValue = (tag: any) => {
  if (tag.value) return tag.value
  if (tag.tag?.value) return tag.tag.value
  if (tag.name) return tag.name
  return 'Unnamed tag'
}

const availableColors = [
  '#ff3b30',
  '#ff9500',
  '#ffcc02',
  '#34c759',
  '#00c7be',
  '#007aff',
  '#5856d6',
  '#af52de',
]

export const TagsPanel: React.FC<TagsPanelProps> = ({
  entityId,
  entityType,
  tags = [],
  loading = false,
  addTag,
  removeTag,
  refreshTags,
}) => {
  const { t } = useTranslation()
  const { panels, closePanel } = useActionPanels()
  const [newTagValue, setNewTagValue] = useState('')
  const [selectedColor, setSelectedColor] = useState(availableColors[0])
  const [isAddingTag, setIsAddingTag] = useState(false)

  const handleAddTag = async () => {
    if (!newTagValue.trim() || !addTag) return

    setIsAddingTag(true)
    try {
      await addTag(newTagValue.trim(), selectedColor)
      setNewTagValue('')
      if (refreshTags) await refreshTags()
    } catch (error) {
      console.error('Error adding tag:', error)
    } finally {
      setIsAddingTag(false)
    }
  }

  const handleRemoveTag = async (tagId: number) => {
    if (!removeTag) return

    try {
      await removeTag(tagId)
      if (refreshTags) await refreshTags()
    } catch (error) {
      console.error('Error removing tag:', error)
    }
  }

  if (loading) {
    return (
      <ActionPanel
        isOpen={panels.tagsOpen}
        onClose={() => closePanel('tagsOpen')}
        title={t('landAnalysis.tagsPanel.title')}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: '#666',
          }}
        >
          {t('landAnalysis.tagsPanel.loadingTags')}
        </div>
      </ActionPanel>
    )
  }

  const getEntityTypeLabel = () => {
    switch (entityType) {
      case 'land':
        return t('landAnalysis.tagsPanel.landTags')
      case 'group_land':
        return t('landAnalysis.tagsPanel.groupLandTags')
      default:
        return t('landAnalysis.tagsPanel.buildingTags')
    }
  }

  return (
    <ActionPanel
      isOpen={panels.tagsOpen}
      onClose={() => closePanel('tagsOpen')}
      title={t('landAnalysis.tagsPanel.title')}
    >
      <TagsContainer>
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#f8f8f8',
            borderRadius: '8px',
          }}
        >
          <h3
            style={{
              margin: '0 0 8px 0',
              fontSize: '14px',
              fontWeight: 600,
              color: '#333',
            }}
          >
            {getEntityTypeLabel()}
          </h3>
          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
            Entity ID: {entityId}
          </p>
        </div>

        {/* Current Tags Display */}
        {tags.length > 0 && (
          <div>
            <h4
              style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              {t('landAnalysis.tagsPanel.currentTags', { count: tags.length })}
            </h4>
            <TagsGrid>
              {tags.map((tag, index) => (
                <TagItem key={tag.id || index} $color={getTagColor(tag)}>
                  <Icon name="tag" size="x-small" />
                  {getTagValue(tag)}
                </TagItem>
              ))}
            </TagsGrid>
          </div>
        )}

        {/* Add New Tag */}
        <div>
          <h4
            style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: 600,
              color: '#333',
            }}
          >
            {t('landAnalysis.tagsPanel.addNewTag')}
          </h4>
          <AddTagContainer>
            <div style={{ flex: 1 }}>
              <Input
                placeholder={t('landAnalysis.tagsPanel.enterTagName')}
                value={newTagValue}
                onChange={(e) => setNewTagValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddTag()
                  }
                }}
              />
            </div>
            <div>
              <select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #e5e5e5',
                  backgroundColor: selectedColor,
                  color: '#ffffff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {availableColors.map((color) => (
                  <option
                    key={color}
                    value={color}
                    style={{ backgroundColor: color }}
                  >
                    {color}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="primary"
              size="small"
              onClick={handleAddTag}
              disabled={!newTagValue.trim() || isAddingTag}
            >
              {isAddingTag ? t('landAnalysis.tagsPanel.adding') : t('landAnalysis.tagsPanel.add')}
            </Button>
          </AddTagContainer>
        </div>

        {/* Tag Management */}
        {tags.length > 0 && (
          <div>
            <h4
              style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              {t('landAnalysis.tagsPanel.manageTags')}
            </h4>
            <TagManagementList>
              {tags.map((tag, index) => (
                <TagManagementItem key={tag.id || index}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <ColorDot $color={getTagColor(tag)} />
                    <span>{getTagValue(tag)}</span>
                  </div>
                  <button
                    onClick={() => tag.id && handleRemoveTag(tag.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ff3b30',
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee'
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <Icon name="x" size="x-small" />
                  </button>
                </TagManagementItem>
              ))}
            </TagManagementList>
          </div>
        )}

        {tags.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#666',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px dashed #ddd',
            }}
          >
            <Icon
              name="tag"
              size="large"
              color="#999"
              style={{ marginBottom: '16px' }}
            />
            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
              {t('landAnalysis.tagsPanel.noTagsAssigned')}
            </p>
            <p style={{ margin: 0, fontSize: '14px' }}>
              {t('landAnalysis.tagsPanel.emptyStateHint')}
            </p>
          </div>
        )}
      </TagsContainer>
    </ActionPanel>
  )
}
